<script setup>
// FORK: in-conversation message search
import { computed, onMounted, onUnmounted } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import { useConversationMessageSearchPanel } from 'dashboard/composables/fork/useConversationMessageSearchPanel';
import { isEditableTarget } from 'dashboard/composables/fork/isEditableTarget';
// FORK: Evolution Go sync contact from MoreActions
import { useInbox } from 'dashboard/composables/useInbox';
import ContactAPI from 'dashboard/api/contacts';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
  // FORK: in-conversation message search
  CMD_SEARCH_IN_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

// No props needed as we're getting currentChat from the store directly
const store = useStore();
const { t } = useI18n();
const { open: openSearchPanel } = useConversationMessageSearchPanel();
// FORK: gate sync menu item to Evolution Go inboxes
const { isEvolutionGoWhatsAppChannel } = useInbox();

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);

const currentChat = computed(() => store.getters.getSelectedChat);

// FORK: track Sync contact poll timers so they clear on unmount
let syncPollTimers = [];

const clearSyncPollTimers = () => {
  syncPollTimers.forEach(timerId => clearTimeout(timerId));
  syncPollTimers = [];
};

const actionMenuItems = computed(() => {
  const items = [];

  if (!currentChat.value.muted) {
    items.push({
      icon: 'i-lucide-volume-off',
      label: t('CONTACT_PANEL.MUTE_CONTACT'),
      action: 'mute',
      value: 'mute',
    });
  } else {
    items.push({
      icon: 'i-lucide-volume-1',
      label: t('CONTACT_PANEL.UNMUTE_CONTACT'),
      action: 'unmute',
      value: 'unmute',
    });
  }

  // FORK: in-conversation message search
  items.push({
    icon: 'i-lucide-search',
    label: t('CONVERSATION.MESSAGE_SEARCH.MENU_LABEL'),
    action: 'search_in_conversation',
    value: 'search_in_conversation',
    title: t('CONVERSATION.MESSAGE_SEARCH.MENU_SHORTCUT'),
  });

  // FORK: Evolution Go — force contact info + avatar sync
  if (isEvolutionGoWhatsAppChannel.value) {
    items.push({
      icon: 'i-lucide-refresh-cw',
      label: t('CONVERSATION.EVOLUTION_GO_SYNC_CONTACT.MENU_LABEL'),
      action: 'sync_contact',
      value: 'sync_contact',
    });
  }

  items.push({
    icon: 'i-lucide-share',
    label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
    action: 'send_transcript',
    value: 'send_transcript',
  });

  return items;
});

const openMessageSearch = () => {
  openSearchPanel();
};

const syncEvolutionGoContact = async () => {
  const contactId = currentChat.value?.meta?.sender?.id;
  const inboxId = currentChat.value?.inbox_id;
  if (!contactId) {
    useAlert(t('CONVERSATION.EVOLUTION_GO_SYNC_CONTACT.ERROR'));
    return;
  }

  try {
    await ContactAPI.evolutionGoSync(contactId, { inboxId });
    useAlert(t('CONVERSATION.EVOLUTION_GO_SYNC_CONTACT.SUCCESS'));
    // Poll several times — force sync may retry Go /user/avatar (slow timeouts).
    clearSyncPollTimers();
    const pollContact = (attempt = 0) => {
      store.dispatch('contacts/show', { id: contactId });
      if (attempt < 5) {
        syncPollTimers.push(setTimeout(() => pollContact(attempt + 1), 5000));
      }
    };
    syncPollTimers.push(setTimeout(() => pollContact(0), 3000));
  } catch (error) {
    const message =
      error?.response?.data?.error ||
      t('CONVERSATION.EVOLUTION_GO_SYNC_CONTACT.ERROR');
    useAlert(message);
  }
};

const handleActionClick = ({ action }) => {
  toggleDropdown(false);

  if (action === 'mute') {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  } else if (action === 'unmute') {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } else if (action === 'search_in_conversation') {
    // FORK: in-conversation message search
    openMessageSearch();
  } else if (action === 'sync_contact') {
    // FORK: Evolution Go contact sync
    syncEvolutionGoContact();
  } else if (action === 'send_transcript') {
    toggleEmailModal();
  }
};

// These functions are needed for the event listeners
const mute = () => {
  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const unmute = () => {
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};

emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);
emitter.on(CMD_SEARCH_IN_CONVERSATION, openMessageSearch);

const onSearchHotkey = event => {
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'f') {
    if (isEditableTarget(event.target)) return;

    event.preventDefault();
    openMessageSearch();
  }
};

onMounted(() => {
  window.addEventListener('keydown', onSearchHotkey);
});

onUnmounted(() => {
  clearSyncPollTimers();
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
  emitter.off(CMD_SEARCH_IN_CONVERSATION, openMessageSearch);
  window.removeEventListener('keydown', onSearchHotkey);
});
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <ResolveAction
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />
    <div
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="toggleDropdown()"
      />
      <DropdownMenu
        v-if="showActionsDropdown"
        :menu-items="actionMenuItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleActionClick"
      />
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
  </div>
</template>
